import { Box, MenuItem, Paper, Select, TextField, Typography } from '@material-ui/core'
import Button from 'components/Buttons'
import { WaoCheckbox, WaoFormContainer, WaoIntegerInput } from 'components/Forms'
import { CompanyFormModel, PicklistItemModel } from 'data/models'
import { ShapeFileModel } from 'data/models/ShapefileModel'
import { Dictionary } from 'data/transform'
import { StyleForm } from 'data/lists/StyleForm'
import { shapeType } from 'enums/shapeType'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import useFormStyles from './useFormStyles'

interface Props {
  item: ShapeFileModel
  forms: CompanyFormModel[]
  shapefileGroupNames: PicklistItemModel[]
  handleCreate?: (raster: ShapeFileModel) => void
  handleUpdate?: (id: number, changes: Dictionary<any>, something?: boolean) => void
  handleCancel?: () => void
  handleDelete?: (id: number) => void
}

const ShapeFileEditForm = ({ item, forms, shapefileGroupNames, handleCreate, handleUpdate, handleCancel, handleDelete }: Props) => {
  const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark')
  const classes = useFormStyles({ isDarkMode })
  const [selectedShapeFile, setSelectedShapeFile] = React.useState<ShapeFileModel>(Object.assign(new ShapeFileModel(), item))
  const [modified, setModified] = React.useState<Dictionary<any>>({})
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const editing = Boolean(selectedShapeFile.Id)
  const dirty = () => 0 < Object.keys(modified).length
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleChange = (name: string) => (value: any) => mutateState(name, value)

  const mutateState = (name: string, value: any) => {
    setModified((prev) => ({ ...prev, [name]: value }))
    setSelectedShapeFile((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      mutateState("Filename", file.name);
      mutateState("Filesize", file.size);
      mutateState("Name", file.name.replace(/\.[^/.]+$/, ""));
      mutateState("IsActive", true);
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext === 'dbf') mutateState("HasDBF", true);
      if (ext === 'prj') mutateState("HasPRJ", true);
      if (ext === 'shx') mutateState("HasSHX", true);
    }
  };

  const isNameEmpty = !selectedShapeFile.Name || selectedShapeFile.Name.trim() === ''

  const handleSubmit = (event: any) => {
    event.preventDefault()
    setIsSubmitting(true);
    const op = editing ? handleUpdate && selectedShapeFile.Id && handleUpdate(selectedShapeFile.Id, selectedShapeFile) : handleCreate && handleCreate(selectedShapeFile)
    Promise.resolve(op).finally(() => setIsSubmitting(false))
  }

  const emptyPlaceholder = <em style={{ color: isDarkMode ? '#6B7280' : '#9CA3AF' }}>-- Select --</em>

  return (
    <WaoFormContainer name="user-edit-form" submit={(e) => handleSubmit(e)}>
      <Box style={{ padding: '16px 16px 0' }}>

        {/* ══ File / General ═════════════════════════════════════ */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <FormattedMessage id="shapefile.section.general" defaultMessage="General" />
          </Typography>

          {!editing && (
            <Box className={classes.fieldGroup}>
              <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} accept=".shp,.dbf,.shx,.prj" />
              <Button.CreateButton label="Select Shapefile" click={() => fileInputRef.current?.click()} fullWidth />
            </Box>
          )}

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="name" defaultMessage="Name" />
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              label=""
              value={selectedShapeFile.Name || ''}
              onChange={(e) => handleChange('Name')(e.target.value)}
              className={classes.input}
              InputLabelProps={{ shrink: false }}
            />
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="description" defaultMessage="Description" />
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              label=""
              value={selectedShapeFile.Description || ''}
              onChange={(e) => handleChange('Description')(e.target.value)}
              className={classes.input}
              InputLabelProps={{ shrink: false }}
            />
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.groupName" defaultMessage="Group Name" />
            </Typography>
            <Select
              fullWidth
              variant="outlined"
              value={selectedShapeFile.GroupNamePicklistId ?? ''}
              onChange={(e) => handleChange('GroupNamePicklistId')(e.target.value)}
              className={classes.select}
              displayEmpty
            >
              <MenuItem value="">{emptyPlaceholder}</MenuItem>
              {shapefileGroupNames?.map((list) => (
                <MenuItem key={String(list.Id)} value={list.Id}>{list.Value || ''}</MenuItem>
              ))}
            </Select>
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.filename" defaultMessage="File Name" />
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              label=""
              value={selectedShapeFile.Filename || ''}
              onChange={(e) => handleChange('Filename')(e.target.value)}
              className={classes.input}
              InputLabelProps={{ shrink: false }}
            />
          </Box>
        </Paper>

        {/* ══ Display Settings ═══════════════════════════════════ */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <FormattedMessage id="shapefile.section.display" defaultMessage="Display Settings" />
          </Typography>

          <Box className={classes.fieldRow}>
            <Box className={classes.fieldGroup}>
              <Typography className={classes.fieldLabel}>
                <FormattedMessage id="shapefile.minZoom" defaultMessage="Min Zoom" />
              </Typography>
              <WaoIntegerInput name="MinimumZoomVisible" value={selectedShapeFile.MinimumZoomVisible?.toString()} handleChange={handleChange} label="" />
            </Box>
            <Box className={classes.fieldGroup}>
              <Typography className={classes.fieldLabel}>
                <FormattedMessage id="shapefile.maxZoom" defaultMessage="Max Zoom" />
              </Typography>
              <WaoIntegerInput name="MaximumZoomVisible" value={selectedShapeFile.MaximumZoomVisible?.toString()} handleChange={handleChange} label="" />
            </Box>
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.type" defaultMessage="Type" />
            </Typography>
            <Select
              fullWidth
              variant="outlined"
              value={selectedShapeFile.StyleTypeId ?? ''}
              onChange={(e) => handleChange('StyleTypeId')(e.target.value)}
              className={classes.select}
              displayEmpty
            >
              <MenuItem value="">{emptyPlaceholder}</MenuItem>
              {Object.keys(shapeType).filter((key) => !isNaN(Number(shapeType[key]))).map((v, i) => (
                <MenuItem key={String(i)} value={i}>{v}</MenuItem>
              ))}
            </Select>
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.displayOrder" defaultMessage="Display Order" />
            </Typography>
            <WaoIntegerInput name="DisplayOrder" value={selectedShapeFile.DisplayOrder?.toString()} handleChange={handleChange} label="" />
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.tooltipFormat" defaultMessage="Tool Tip Format" />
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              label=""
              value={selectedShapeFile.ToolTipFormat || ''}
              onChange={(e) => handleChange('ToolTipFormat')(e.target.value)}
              className={classes.input}
              InputLabelProps={{ shrink: false }}
            />
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.viewDynamicExpression" defaultMessage="View Dynamic Expression" />
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              label=""
              value={selectedShapeFile.ViewDynamicExpression || ''}
              onChange={(e) => handleChange('ViewDynamicExpression')(e.target.value)}
              className={classes.input}
              InputLabelProps={{ shrink: false }}
            />
          </Box>
        </Paper>

        {/* ══ Form Locks & Styles ════════════════════════════════ */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <FormattedMessage id="shapefile.section.forms" defaultMessage="Form Locks & Styles" />
          </Typography>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.lockToCompanyForm" defaultMessage="Lock To Company Form" />
            </Typography>
            <Select
              fullWidth
              variant="outlined"
              value={selectedShapeFile.LockToCompanyFormId ?? ''}
              onChange={(e) => handleChange('LockToCompanyFormId')(e.target.value)}
              className={classes.select}
              displayEmpty
            >
              <MenuItem value="">{emptyPlaceholder}</MenuItem>
              {forms.map((list) => (
                <MenuItem key={String(list.Id)} value={list.Id}>{list.Name || ''}</MenuItem>
              ))}
            </Select>
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.styleCompanyForm" defaultMessage="Style Company Form" />
            </Typography>
            <Select
              fullWidth
              variant="outlined"
              value={selectedShapeFile.StyleCompanyFormId ?? ''}
              onChange={(e) => handleChange('StyleCompanyFormId')(e.target.value)}
              className={classes.select}
              displayEmpty
            >
              <MenuItem value="">{emptyPlaceholder}</MenuItem>
              {StyleForm.map((entry) => (
                <MenuItem key={String(entry.value)} value={entry.value}>{entry.name}</MenuItem>
              ))}
            </Select>
          </Box>
        </Paper>

        {/* ══ File Info ═════════════════════════════════════════ */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <FormattedMessage id="shapefile.section.fileInfo" defaultMessage="File Info" />
          </Typography>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.filesize" defaultMessage="File Size (Bytes)" />
            </Typography>
            <WaoIntegerInput name="Filesize" value={selectedShapeFile.Filesize?.toString()} handleChange={handleChange} label="" disabled />
          </Box>

          <Box className={classes.fieldGroup}>
            <Typography className={classes.fieldLabel}>
              <FormattedMessage id="shapefile.lineBuffer" defaultMessage="Line Buffer (meters)" />
            </Typography>
            <WaoIntegerInput name="LineBufferInMeters" value={selectedShapeFile.LineBufferInMeters?.toString()} handleChange={handleChange} label="" />
          </Box>
        </Paper>

        {/* ══ Shapefile Components ═══════════════════════════════ */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle}>
            <FormattedMessage id="shapefile.section.components" defaultMessage="Shapefile Components" />
          </Typography>

          <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <WaoCheckbox name="ClusteringEnabled" value={selectedShapeFile.ClusteringEnabled} handleChange={handleChange} label="Clustering Points" />
            <WaoCheckbox name="DefaultVisibleOnMap" value={selectedShapeFile.DefaultVisibleOnMap} handleChange={handleChange} label="Default Visible On Map" />
            <WaoCheckbox name="HasPointPNGIcon" value={selectedShapeFile.HasPointPNGIcon} handleChange={handleChange} label="Has Point PNG Icon" />
            <WaoCheckbox name="IsActive" value={selectedShapeFile.IsActive} handleChange={handleChange} label="Is Active" />
            <WaoCheckbox name="HasDBF" value={selectedShapeFile.HasDBF} handleChange={handleChange} label="Has DBF" />
            <WaoCheckbox name="HasPRJ" value={selectedShapeFile.HasPRJ} handleChange={handleChange} label="Has PRJ" />
            <WaoCheckbox name="HasSHX" value={selectedShapeFile.HasSHX} handleChange={handleChange} label="Has SHX" />
            <WaoCheckbox name="HasSQLiteDB" value={selectedShapeFile.HasSQLiteDB} handleChange={handleChange} label="Has SQLite DB" />
          </Box>
        </Paper>

      </Box>

      {/* ── Action bar ── */}
      <Box className={classes.actionBar}>
        {editing && (
          <Button.DeleteButton click={() => handleDelete && handleDelete(selectedShapeFile.Id)} />
        )}
        <Box className={classes.actionBarSpacer} />
        <Box className={classes.actionBarRight}>
          <Button.CancelButton click={() => handleCancel && handleCancel()} />
          <Button.SubmitButton
            disabled={!dirty() || isSubmitting || isNameEmpty}
            label={editing ? 'Update' : 'Create'}
          />
        </Box>
      </Box>
    </WaoFormContainer>
  )
}

export default ShapeFileEditForm
